import React, { useEffect, useState } from 'react';
import { Search, Shield, ShieldOff, Trash2, UserCheck, UserX } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { listUsers, patchUser, deleteUser } from '../../lib/admin';

export default function AdminUsers() {
    const [data, setData] = useState({ users: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [busy, setBusy] = useState(null);

    const load = async (search = '') => {
        setLoading(true);
        try {
            const r = await listUsers(search ? { q: search } : {});
            setData(r);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const toggleSuspend = async (u) => {
        setBusy(u.id);
        try {
            const updated = await patchUser(u.id, { is_suspended: !u.is_suspended });
            setData({ ...data, users: data.users.map((x) => x.id === u.id ? updated : x) });
        } finally { setBusy(null); }
    };

    const toggleRole = async (u) => {
        const newRole = u.role === 'admin' ? 'user' : 'admin';
        if (!window.confirm(`Cambiar el rol de ${u.email} a ${newRole}?`)) return;
        setBusy(u.id);
        try {
            const updated = await patchUser(u.id, { role: newRole });
            setData({ ...data, users: data.users.map((x) => x.id === u.id ? updated : x) });
        } finally { setBusy(null); }
    };

    const removeUser = async (u) => {
        if (!window.confirm(`Eliminar definitivamente a ${u.email}? Esta acción no se puede deshacer.`)) return;
        setBusy(u.id);
        try {
            await deleteUser(u.id);
            setData({ ...data, users: data.users.filter((x) => x.id !== u.id), total: data.total - 1 });
        } catch (e) {
            alert(e?.response?.data?.detail || 'Error');
        } finally { setBusy(null); }
    };

    return (
        <AdminLayout title="Usuarios">
            <div className="mb-4 flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && load(q)}
                        placeholder="Buscar por email o nombre…"
                        className="w-full h-10 pl-9 pr-3 bg-white border border-gray-200 rounded-xl text-sm"
                        data-testid="admin-users-search"
                    />
                </div>
                <button onClick={() => load(q)} className="h-10 px-4 bg-brand text-white text-sm font-bold rounded-xl" data-testid="admin-users-search-btn">Buscar</button>
                <span className="ml-auto text-xs text-gray-500" data-testid="admin-users-total">Total: {data.total}</span>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-10 flex justify-center"><div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /></div>
                ) : (
                    <table className="w-full text-sm" data-testid="admin-users-table">
                        <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 tracking-wider">
                            <tr>
                                <th className="px-4 py-3 text-left">Email</th>
                                <th className="px-4 py-3 text-left">Nombre</th>
                                <th className="px-4 py-3 text-left">Rol</th>
                                <th className="px-4 py-3 text-left">Verificado</th>
                                <th className="px-4 py-3 text-left">Estado</th>
                                <th className="px-4 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.users.map((u) => (
                                <tr key={u.id} className="border-t border-gray-100" data-testid={`admin-user-${u.id}`}>
                                    <td className="px-4 py-3 font-medium text-brand-navy max-w-[260px] truncate">{u.email}</td>
                                    <td className="px-4 py-3 text-gray-600">{u.name || '—'}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${u.role === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-600'}`}>{u.role || 'user'}</span>
                                    </td>
                                    <td className="px-4 py-3">{u.email_verified ? '✅' : '⏳'}</td>
                                    <td className="px-4 py-3">{u.is_suspended ? <span className="text-red-600 font-semibold">Suspendido</span> : <span className="text-emerald-600">Activo</span>}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => toggleSuspend(u)} disabled={busy === u.id} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center" title={u.is_suspended ? 'Reactivar' : 'Suspender'} data-testid={`admin-user-suspend-${u.id}`}>
                                                {u.is_suspended ? <UserCheck className="w-4 h-4 text-emerald-600" /> : <UserX className="w-4 h-4 text-orange-500" />}
                                            </button>
                                            <button onClick={() => toggleRole(u)} disabled={busy === u.id} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center" title="Cambiar rol" data-testid={`admin-user-role-${u.id}`}>
                                                {u.role === 'admin' ? <ShieldOff className="w-4 h-4 text-gray-500" /> : <Shield className="w-4 h-4 text-violet-500" />}
                                            </button>
                                            <button onClick={() => removeUser(u)} disabled={busy === u.id} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center" title="Eliminar" data-testid={`admin-user-delete-${u.id}`}>
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {data.users.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">Sin resultados</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </AdminLayout>
    );
}
